import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, ArrowRight, Award, Target, Heart } from 'lucide-react';
import axios from 'axios';

const API = '/api';

const OurTeam = () => {
  const { t, language } = useLanguage();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = language === 'en'
      ? 'Our Team | Credit Auto Reset - Auto Financing Experts Quebec'
      : 'Notre Équipe | Credit Auto Reset - Experts Financement Auto Québec';
    document.querySelector('meta[name="description"]')?.setAttribute('content',
      language === 'en'
        ? 'Meet the Credit Auto Reset team. Experienced auto financing and credit recovery specialists serving Quebec for over 19 years.'
        : 'Rencontrez l\'équipe Credit Auto Reset. Spécialistes expérimentés en financement auto et rétablissement de crédit au Québec depuis plus de 19 ans.'
    );
  }, [language]);

  // Default team members (fallback)
  const defaultTeamMembers = [
    {
      name: 'Jack Oundjian',
      role_en: 'President',
      role_fr: 'Président',
      image_url: 'https://static.wixstatic.com/media/fdc02d_a246b4a6faf34d77b821e50316706bfe~mv2.png'
    },
    {
      name: 'Naseeb Toraubally',
      role_en: 'VP Operations',
      role_fr: 'VP aux opérations',
      image_url: 'https://static.wixstatic.com/media/fdc02d_d2a5df089782472fb0967569d5c1c74f~mv2.png'
    },
    {
      name: 'Alexandre Paquin',
      role_en: 'VP Sales',
      role_fr: 'VP ventes',
      image_url: 'https://static.wixstatic.com/media/fdc02d_14a1fe9281d646a6a70768606038e4ef~mv2.png'
    },
    {
      name: 'René Arbour',
      role_en: 'Sales Director',
      role_fr: 'Directeur des ventes',
      image_url: 'https://static.wixstatic.com/media/fdc02d_1ba1ed9a285140a5adc8b587f67e4126~mv2.png'
    },
    {
      name: 'Karyn Guillemette',
      role_en: 'Auto Financing / Credit Recovery Expert',
      role_fr: 'Expert en financement Automobile/Redressement Credit',
      image_url: ''
    },
    {
      name: 'Melanie Taillefer',
      role_en: 'Customer Service Manager',
      role_fr: 'Responsable Service à la clientèle',
      image_url: ''
    },
    {
      name: 'Yacouba Traoré',
      role_en: 'Auto Financing / Credit Recovery Expert',
      role_fr: 'Expert en financement Automobile/Redressement Credit',
      image_url: ''
    },
    {
      name: 'Jean-François Gosselin',
      role_en: 'Auto Financing / Credit Recovery Expert',
      role_fr: 'Expert en financement Automobile/Redressement Credit',
      image_url: ''
    },
    {
      name: 'Janie Leblanc',
      role_en: 'Auto Financing / Credit Recovery Expert',
      role_fr: 'Expert en financement Automobile/Redressement Credit',
      image_url: ''
    },
    {
      name: 'Francois Lachance',
      role_en: 'Auto Financing / Credit Recovery Expert',
      role_fr: 'Expert en financement Automobile/Redressement Credit',
      image_url: ''
    },
    {
      name: 'Dawson Triantopoulos-Massicotte',
      role_en: 'Financing Expert',
      role_fr: 'Expert en financement',
      image_url: ''
    },
    {
      name: 'Jun Xiang',
      role_en: 'Operations Coordinator / Buyer',
      role_fr: 'Coordonnateur aux Opérations / Acheteur',
      image_url: ''
    }
  ];

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await axios.get(`${API}/team`);
        if (response.data && response.data.length > 0) {
          setTeamMembers(response.data);
        } else {
          setTeamMembers(defaultTeamMembers);
        }
      } catch (error) {
        console.error('Failed to fetch team:', error);
        setTeamMembers(defaultTeamMembers);
      }
      setLoading(false);
    };
    fetchTeam();
  }, []);

  const getRole = (member) => {
    return language === 'en' ? (member.role_en || member.role) : (member.role_fr || member.role);
  };

  const getImageUrl = (member) => {
    if (member.image_url) return member.image_url;
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=1a5c3b&color=fff&size=400`;
  };

  const isPlaceholderImage = (member) => {
    return !member.image_url || member.image_url.includes('ui-avatars.com');
  };

  const values = [
    {
      icon: Award,
      title: language === 'en' ? 'Excellence' : 'Excellence',
      description: language === 'en' ? 'We strive for the highest standards in everything we do' : 'Nous visons les plus hauts standards dans tout ce que nous faisons'
    },
    {
      icon: Target,
      title: language === 'en' ? 'Results-Driven' : 'Axé sur les résultats',
      description: language === 'en' ? 'Focused on getting you approved and on the road' : 'Concentré à vous faire approuver et sur la route'
    },
    {
      icon: Heart,
      title: language === 'en' ? 'Compassion' : 'Compassion',
      description: language === 'en' ? 'Understanding your unique situation with empathy' : 'Comprendre votre situation unique avec empathie'
    }
  ];

  return (
    <div className="min-h-screen pt-20 pb-16">
      {/* Header */}
      <section className="bg-gradient-to-r from-[#1a5c3b] to-[#16a085] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Users className="w-12 h-12 text-white mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t.team.title}
          </h1>
          <p className="text-white/90 text-lg max-w-2xl mx-auto">
            {t.team.description}
          </p>
        </div>
      </section>

      {/* Team Members - Hierarchical Layout */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-[#1a5c3b] border-t-transparent rounded-full mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading team...</p>
            </div>
          ) : (
            <>
              {/* President - Top Level */}
              {teamMembers.length > 0 && (
                <div className="flex justify-center mb-8">
                  <Card className="overflow-hidden border hover:border-[#1a5c3b] transition-all duration-300 w-48">
                    <div className="relative h-40 overflow-hidden bg-white flex items-center justify-center">
                      <img
                        src={getImageUrl(teamMembers[0])}
                        alt={teamMembers[0].name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <CardContent className="p-3 text-center bg-[#1a5c3b]">
                      <h3 className="text-sm font-bold text-white">{teamMembers[0].name}</h3>
                      <p className="text-white/90 text-xs">{getRole(teamMembers[0])}</p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* VPs - Second Level */}
              {teamMembers.length > 1 && (
                <div className="flex justify-center gap-6 mb-8 flex-wrap">
                  {teamMembers.slice(1, 3).map((member, index) => (
                    <Card key={member.id || index} className="overflow-hidden border hover:border-[#1a5c3b] transition-all duration-300 w-44">
                      <div className="relative h-36 overflow-hidden bg-white flex items-center justify-center">
                        <img
                          src={getImageUrl(member)}
                          alt={member.name}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <CardContent className="p-2 text-center bg-[#1a5c3b]/90">
                        <h3 className="text-sm font-bold text-white">{member.name}</h3>
                        <p className="text-white/90 text-xs">{getRole(member)}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Rest of Team - Grid Layout */}
              {teamMembers.length > 3 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
                  {teamMembers.slice(3).map((member, index) => {
                    const showInitials = isPlaceholderImage(member);
                    return (
                      <Card key={member.id || index} className="overflow-hidden border hover:border-[#1a5c3b] transition-all duration-300">
                        <div className="relative h-32 overflow-hidden bg-white flex items-center justify-center">
                          {showInitials ? (
                            <div className="w-16 h-16 rounded-full bg-[#1a5c3b] flex items-center justify-center">
                              <span className="text-white text-lg font-bold">
                                {member.name.split(' ').map(n => n[0]).join('')}
                              </span>
                            </div>
                          ) : (
                            <img
                              src={getImageUrl(member)}
                              alt={member.name}
                              className="w-full h-full object-contain"
                            />
                          )}
                        </div>
                        <CardContent className="p-2 text-center">
                          <h3 className="text-sm font-bold text-gray-900">{member.name}</h3>
                          <p className="text-[#1a5c3b] text-xs leading-tight">{getRole(member)}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-12">
            {language === 'en' ? 'Our Values' : 'Nos valeurs'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-[#1a5c3b] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#1a5c3b] to-[#16a085]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            {language === 'en' ? 'Ready to Work With Our Team?' : 'Prêt à travailler avec notre équipe?'}
          </h2>
          <p className="text-white/90 mb-8">
            {language === 'en' ? 'Let our experts help you find the perfect financing solution' : 'Laissez nos experts vous aider à trouver la solution de financement parfaite'}
          </p>
          <Link to="/demander-un-pret-auto">
            <Button size="lg" className="bg-white text-[#1a5c3b] hover:bg-gray-100 font-semibold">
              {language === 'en' ? 'Start Your Application' : 'Commencer votre demande'}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default OurTeam;
